import React from 'react';
import type { ComponentConfig } from '@puckeditor/core';
import styled from 'styled-components';

import { ColorRoleField } from '../fields/widgets/color-role-field';
import { SurfaceColorField, SURFACE_COLOR_FIELD_AI_SCHEMA } from '../fields/widgets/surface-color-field';
import { createSpacingFields } from '../fields/spacing-fields';
import { CmsRichTextMenu, CmsTextRichTextMenu, cmsRichTextSelector } from '../fields/widgets/richtext-menu';
import { interpolateHtml } from '../interpolate-html';
import { getCmsVariables } from '../interpolation';
import { sanitizeCmsHtml } from '../sanitize-html';
import {
  BLOCK_WIDTHS,
  BODY_LINE_HEIGHT,
  BODY_TEXT_SIZES,
  FONT_ROLES,
  lookup,
  MAX_WIDTHS,
  NO_SPACING,
  resolveTextColor,
  resolveSurfaceColor,
  spacingToCss,
  SURFACE_COLOR_ROLES,
  TEXT_COLOR_ROLES,
} from '../fields/tokens';
import type { BlockWidthOption, BodyTextSize, BoxSpacing, FontRole, MaxWidthOption } from '../fields/tokens';

export const DEFAULT_PARAGRAPH_HTML = '<p>Start writing here...</p>';

const TEXT_HEADING_TAG_PATTERN = /<(\/?)h[1-6]\b/gi;

/**
 * Text has no heading semantics, even when saved JSON bypasses the editor's `heading:false` gate.
 * Downgrading each heading tag to a paragraph preserves its block boundary; stripping tags would
 * concatenate adjacent headings into one text run. Only the tag NAME changes: attributes stay on
 * the resulting `<p>` for the shared sanitizer to keep (safe text alignment) or reject (events,
 * classes, compound styles). This runs after interpolation so a variable cannot inject a heading
 * either, and before the shared sanitizer so every other consumer's heading policy remains untouched.
 */
function coerceTextHeadingsToParagraphs(html: string): string {
  return html.replace(TEXT_HEADING_TAG_PATTERN, '<$1p');
}

type ParagraphTextSize = BodyTextSize | 'system';

export type ParagraphProps = {
  text: string;
  size: ParagraphTextSize;
  font: FontRole;
  color: string;
  /** Either a surface role key (e.g. `'surface'`) or a custom hex (e.g. `'#ff0000'`). */
  background: string;
  width: BlockWidthOption;
  maxWidth: MaxWidthOption;
  padding: BoxSpacing;
};

/** See heading.tsx for the `--cms-*` over `--dcm-*` precedence this relies on. */
const StyledParagraph = styled.div`
  margin: 0;
  background: var(--cms-paragraph-background, ${SURFACE_COLOR_ROLES.none});
  color: var(--cms-paragraph-color, ${TEXT_COLOR_ROLES.text});
  font-family: var(--cms-paragraph-font-family, ${FONT_ROLES.body});
  font-size: var(--cms-paragraph-font-size, ${BODY_TEXT_SIZES.lg});
  line-height: var(--cms-paragraph-line-height, ${BODY_LINE_HEIGHT});
  width: var(--cms-paragraph-width, ${BLOCK_WIDTHS.fill});
  max-width: var(--cms-paragraph-max-width, ${MAX_WIDTHS.none});
  padding: var(--cms-paragraph-padding, 0px);

  > :first-child,
  .ProseMirror > :first-child {
    margin-top: 0;
  }

  > :last-child,
  .ProseMirror > :last-child {
    margin-bottom: 0;
  }

  a {
    color: var(--dcm-cms-paragraph-link-color, var(--dcm-color-link, currentColor));
    text-decoration: underline;
  }
`;

const SPACING_FIELDS = createSpacingFields();

const SIZE_OPTIONS: { label: string; value: ParagraphTextSize }[] = [
  { label: 'System default', value: 'system' },
  { label: 'Extra small', value: 'xs' },
  { label: 'Small', value: 'sm' },
  { label: 'Medium', value: 'md' },
  { label: 'Large', value: 'lg' },
  { label: 'Extra large', value: 'xl' },
  { label: '2X large', value: 'xxl' },
];

export const paragraphConfig: ComponentConfig<ParagraphProps> = {
  label: 'Text',
  ai: {
    instructions:
      'A rich-text block for descriptions, content paragraphs, and explanatory text. Supports formatting, lists, alignment, links, and CMS variable tokens.',
  },
  fields: {
    text: {
      type: 'richtext',
      label: 'Text',
      contentEditable: true,
      renderMenu: CmsTextRichTextMenu,
      renderInlineMenu: CmsRichTextMenu,
      tiptap: { selector: cmsRichTextSelector },
      options: {
        heading: false,
        link: { openOnClick: false },
      },
    },
    size: {
      type: 'select',
      label: 'Size',
      options: SIZE_OPTIONS,
      ai: {
        schema: { type: 'string', enum: SIZE_OPTIONS.map(({ value }) => value) },
        instructions: "Body text size from the storefront's shared scale. 'lg' suits most page copy.",
      },
    },
    font: {
      type: 'radio',
      label: 'Font',
      options: [
        { label: 'Heading', value: 'heading' },
        { label: 'Body', value: 'body' },
      ],
      ai: {
        schema: { type: 'string', enum: ['heading', 'body'] },
        instructions: "Which of the merchant's two configured fonts to use. Body copy should almost always use 'body'.",
      },
    },
    background: {
      type: 'custom',
      label: 'Background',
      render: SurfaceColorField,
      ai: {
        schema: SURFACE_COLOR_FIELD_AI_SCHEMA,
        instructions:
          "Optional fill behind the text. Set to a surface role ('none', 'surface', 'muted') so it follows merchant branding; use a hex value only when a specific brand color is required.",
      },
    },
    color: {
      type: 'custom',
      label: 'Text color',
      render: ColorRoleField,
      ai: {
        schema: {
          type: 'string',
          description:
            "Either a theme color role ('text', 'secondary', 'muted', 'inverted', 'link') or a hex value such as #2c3236. Prefer a role so the text follows merchant branding.",
        },
      },
    },
    width: {
      type: 'radio',
      label: 'Width',
      options: [
        { label: 'Fit', value: 'fit' },
        { label: 'Fill', value: 'fill' },
      ],
      ai: { schema: { type: 'string', enum: ['fit', 'fill'] } },
    },
    maxWidth: {
      type: 'radio',
      label: 'Max width',
      options: [
        { label: 'Narrow', value: 'narrow' },
        { label: 'Normal', value: 'normal' },
        { label: 'None', value: 'none' },
      ],
      ai: {
        schema: { type: 'string', enum: ['narrow', 'normal', 'none'] },
        instructions: 'Constrains line length for readability. Prefer narrow or normal for long-form copy.',
      },
    },
    padding: {
      type: 'object',
      label: 'Padding',
      objectFields: SPACING_FIELDS,
    },
  },
  defaultProps: {
    text: DEFAULT_PARAGRAPH_HTML,
    size: 'system',
    font: 'body',
    color: 'text',
    background: 'none',
    width: 'fill',
    maxWidth: 'none',
    padding: NO_SPACING,
  },
  render: ({
    text = DEFAULT_PARAGRAPH_HTML,
    size = 'system',
    font = 'body',
    color = 'text',
    background = 'none',
    width = 'fill',
    maxWidth = 'none',
    padding,
    puck,
  }) => {
    const content = text as unknown;
    const style = {
      '--cms-paragraph-background': resolveSurfaceColor(background),
      '--cms-paragraph-color': resolveTextColor(color),
      '--cms-paragraph-font-family': lookup(FONT_ROLES, font, FONT_ROLES.body),
      ...(size === 'system' ? {} : { '--cms-paragraph-font-size': lookup(BODY_TEXT_SIZES, size, BODY_TEXT_SIZES.lg) }),
      '--cms-paragraph-width': lookup(BLOCK_WIDTHS, width, BLOCK_WIDTHS.fill),
      '--cms-paragraph-max-width': lookup(MAX_WIDTHS, maxWidth, MAX_WIDTHS.none),
      '--cms-paragraph-padding': spacingToCss(padding),
    } as React.CSSProperties;

    // Inside <Puck>, the built-in richtext field transform replaces the saved HTML string with a
    // live Tiptap ReactNode before this render function runs. That node is the editor itself: it must
    // never pass through interpolation or sanitisation, which would stringify it and risk replacing
    // the literal {{tokens}} in the authoring buffer with sample values. Puck's production <Render>
    // would also wrap a `richtext` field; both renderer configs use createProductionComponents to
    // expose this one production field as `textarea`, making the raw saved HTML reach the branch below.
    if (typeof content !== 'string') {
      return <StyledParagraph style={style}>{content as React.ReactNode}</StyledParagraph>;
    }

    const interpolatedHtml = interpolateHtml(content, getCmsVariables(puck));
    const renderedHtml = sanitizeCmsHtml(coerceTextHeadingsToParagraphs(interpolatedHtml));
    return <StyledParagraph style={style} dangerouslySetInnerHTML={{ __html: renderedHtml }} />;
  },
};
